/**
 * Block 12 PR-B Phase B3 Part C Step 14 — anketaUiMapping.
 *
 * Mapping `CalculatorField.kind` → UI component picker. Используется
 * `<SectionParams>` (Step 16) для рендера правильного компонента под каждое
 * поле methodology.fields[].
 *
 * Rules:
 *   - `'number'` → `<NumberInput>` (B1 frozen).
 *   - `'boolean'` → `<RadioCard>` 2-опции (Да/Нет, P-019 вертикальный список).
 *   - `'select'`:
 *     - `options.length ≤ 3` → `<RadioCard>` (компактно, не требует bottom sheet).
 *     - `options.length > 3` → `<PickerSheet>` (B1 frozen, для длинных списков).
 *   - `'text'` → `<TextInput>` (custom, минимальный wrap над `<input type="text">`).
 *
 * Reference: ADR-0020 §Q1 (methodology.fields[] driven), B1 frozen components.
 */

import type { CalculatorAnketaType, CalculatorMethodology } from '@pomoshnik/shared';

export type AnketaFieldUiKind = 'number' | 'radio' | 'picker' | 'text';

/**
 * `CalculatorField` exact shape — derive from methodology Zod type. Используется
 * как input для `pickFieldComponent` + `<SectionParams>` rendering.
 */
export type AnketaField = CalculatorMethodology['fields'][number];

/**
 * Decision boundary: RadioCard для select с ≤3 опциями, PickerSheet иначе.
 * Per P-019 (вертикальные списки) — 4+ RadioCards визуально перегружают viewport.
 */
const RADIO_MAX_OPTIONS = 3;

export function pickFieldComponent(field: AnketaField): AnketaFieldUiKind {
  switch (field.kind) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'radio';
    case 'text':
      return 'text';
    case 'select': {
      const optionsCount = field.options?.length ?? 0;
      return optionsCount <= RADIO_MAX_OPTIONS ? 'radio' : 'picker';
    }
    default:
      // CalculatorFieldSchema z.enum уже narrowing — этот branch unreachable.
      throw new Error(`pickFieldComponent: unknown field kind ${JSON.stringify(field.kind)}`);
  }
}

/**
 * For `'boolean'` fields рендер RadioCard с 2 фиксированных опциями.
 * Returns canonical labels для UI consistency (server JSON не передаёт options
 * для boolean — Phase A2 contract).
 */
export const BOOLEAN_OPTIONS: readonly string[] = ['Да', 'Нет'];

/**
 * Маппинг UI-label («Да»/«Нет») → state value (true/false) и обратно.
 * Используется `<SectionParams>` при `kind='boolean'`.
 */
export function booleanFromLabel(label: string): boolean | undefined {
  if (label === 'Да') return true;
  if (label === 'Нет') return false;
  return undefined;
}

export function booleanToLabel(value: boolean | undefined): string | undefined {
  if (value === true) return 'Да';
  if (value === false) return 'Нет';
  return undefined;
}

/**
 * Маппинг UI-label → Zod schema value для `kind='select'` полей, у которых
 * label на русском (Прямая/Угловая, Открытый/Встроенный) не совпадает с
 * underlying schema type (boolean / 'open'|'built-in').
 *
 * Без этого маппинга `<SectionParams>` пробрасывал бы строки русских лейблов
 * прямо в Zod `.safeParse(...)` и валидация Kitchen.isAngular / cabinet.isAngular /
 * kitchen.fridgeType / balcony.isAngular падала бы — «Сохранить проект» отключалась
 * на этих анкетах (Block 12 audit 2026-05-15 Bug #4 + #15).
 *
 * Round-trip контракт:
 *   toSchema(label) === schemaValue, toLabel(schemaValue) === label
 *
 * Reference: Block_12_audit_2026-05-15.md Bug #4 / hotfix TZ Step 5.
 */
interface SelectFieldMapping {
  toSchema: (label: string) => unknown;
  toLabel: (value: unknown) => string;
}

type SelectMappingsByType = {
  [type in CalculatorAnketaType]?: Record<string, SelectFieldMapping>;
};

/**
 * countertopSheetLen label↔number mapping для Kitchen + BathTabletop.
 * Methodology JSON options — display labels («3000 мм» / «4000 мм» per PR-A1
 * ADR-0033); schema хранит number (3000 / 4000).
 *
 * PR-A1 (ADR-0033): лейблы переименованы «3 м (3000 мм)» → «3000 мм». Schema
 * value остаётся number → legacy persisted drafts совместимы без миграции
 * (`toLabel(3000)` ресолвится в новый лейбл, старый «3 м (3000 мм)» больше не
 * генерируется).
 */
const COUNTERTOP_SHEET_LEN_MAPPING: SelectFieldMapping = {
  toSchema: (label) => {
    if (label === '3000 мм') return 3000;
    if (label === '4000 мм') return 4000;
    return label;
  },
  toLabel: (value) => {
    if (value === 3000) return '3000 мм';
    if (value === 4000) return '4000 мм';
    return '';
  },
};

const SELECT_MAPPINGS: SelectMappingsByType = {
  kitchen: {
    isAngular: {
      toSchema: (label) => label === 'Угловая',
      toLabel: (value) => {
        if (value === true) return 'Угловая';
        if (value === false) return 'Прямая';
        return '';
      },
    },
    fridgeType: {
      toSchema: (label) => (label === 'Встроенный' ? 'built-in' : 'open'),
      toLabel: (value) => {
        if (value === 'built-in') return 'Встроенный';
        if (value === 'open') return 'Открытый';
        return '';
      },
    },
    countertopSheetLen: COUNTERTOP_SHEET_LEN_MAPPING,
  },
  cabinet: {
    isAngular: {
      toSchema: (label) => label === 'Угловой',
      toLabel: (value) => {
        if (value === true) return 'Угловой';
        if (value === false) return 'Прямой';
        return '';
      },
    },
  },
  'bath-tabletop': {
    countertopSheetLen: COUNTERTOP_SHEET_LEN_MAPPING,
  },
  balcony: {
    isAngular: {
      toSchema: (label) => label === 'Угловой',
      toLabel: (value) => {
        if (value === true) return 'Угловой';
        if (value === false) return 'Прямой';
        return '';
      },
    },
  },
};

/**
 * UI-label → schema value. Для unknown anketa/field — graceful passthrough
 * (возвращает label как есть), чтобы поле без маппинга продолжало работать
 * как string-select (existing B3 behavior).
 */
export function selectToSchemaValue(
  anketaType: CalculatorAnketaType,
  fieldName: string,
  label: string,
): unknown {
  const mapping = SELECT_MAPPINGS[anketaType]?.[fieldName];
  return mapping ? mapping.toSchema(label) : label;
}

/**
 * Schema value → UI-label. Для unknown anketa/field — `String(value)` fallback.
 * Используется `<SectionParams>` чтобы отрендерить активный RadioCard.
 */
export function schemaValueToLabel(
  anketaType: CalculatorAnketaType,
  fieldName: string,
  value: unknown,
): string {
  const mapping = SELECT_MAPPINGS[anketaType]?.[fieldName];
  if (mapping) return mapping.toLabel(value);
  return value === undefined || value === null ? '' : String(value);
}
